import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileSpreadsheet, Download, Loader2, 
  Check, X, AlertCircle, ChevronRight, Store, ShoppingBag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../utils/format';

const ProductImport = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: importing, 4: complete
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState('Importiert');

  const supportedFormats = [
    { name: 'Standard CSV', icon: FileSpreadsheet, desc: 'Eigene CSV mit deutschen Spaltenüberschriften' },
    { name: 'Shopify', icon: ShoppingBag, desc: 'Export aus Shopify Admin' },
    { name: 'WooCommerce', icon: Store, desc: 'Export aus WordPress/WooCommerce' },
    { name: 'Excel', icon: FileSpreadsheet, desc: '.xlsx oder .xls Dateien' },
  ];

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const extension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(extension)) {
      toast.error('Nur CSV und Excel-Dateien werden unterstützt');
      return;
    }
    
    setFile(selectedFile);
    await previewFile(selectedFile);
  };

  const previewFile = async (selectedFile) => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await axiosInstance.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(response.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Lesen der Datei');
      setFile(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    setStep(3);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('update_existing', updateExisting.toString());
    formData.append('default_category', defaultCategory);
    
    try {
      const response = await axiosInstance.post('/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(response.data);
      setStep(4);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} Produkt(e) erfolgreich importiert!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
      setStep(2);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async (format) => {
    try {
      const response = await axiosInstance.get(`/import/template?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import_template_${format}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFormatBadge = (format) => {
    const colors = {
      shopify: 'bg-green-100 text-green-800',
      woocommerce: 'bg-purple-100 text-purple-800',
      standard: 'bg-blue-100 text-blue-800'
    };
    return colors[format] || colors.standard;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/vendor-dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold">Produkte importieren</h1>
              <p className="text-sm text-gray-600">CSV, Excel, Shopify, WooCommerce</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-[#8B7355] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <Check className="h-5 w-5" /> : s}
              </div>
              {i < 3 && (
                <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-[#8B7355]' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Supported Formats */}
            <Card>
              <CardHeader>
                <CardTitle>Unterstützte Formate</CardTitle>
                <CardDescription>
                  Wir erkennen das Format automatisch anhand der Spaltenüberschriften
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {supportedFormats.map((format) => (
                    <div key={format.name} className="flex items-start gap-3 p-3 border rounded-lg">
                      <format.icon className="h-6 w-6 text-gray-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">{format.name}</p>
                        <p className="text-sm text-gray-500">{format.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Upload Area */}
            <Card>
              <CardHeader>
                <CardTitle>Datei hochladen</CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <Upload className="h-12 w-12 text-gray-400 mb-4" />
                  <span className="text-lg font-medium text-gray-600">Datei hierher ziehen</span>
                  <span className="text-sm text-gray-500">oder klicken zum Auswählen</span>
                  <span className="text-xs text-gray-400 mt-2">CSV, XLSX, XLS (max. 10MB)</span>
                </label>
              </CardContent>
            </Card>

            {/* Download Templates */}
            <Card>
              <CardHeader>
                <CardTitle>Vorlagen herunterladen</CardTitle>
                <CardDescription>
                  Laden Sie eine leere Vorlage herunter und füllen Sie diese mit Ihren Produkten
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => downloadTemplate('standard')}>
                    <Download className="h-4 w-4 mr-2" />
                    Standard CSV
                  </Button>
                  <Button variant="outline" onClick={() => downloadTemplate('shopify')}>
                    <Download className="h-4 w-4 mr-2" />
                    Shopify Format
                  </Button>
                  <Button variant="outline" onClick={() => downloadTemplate('woocommerce')}>
                    <Download className="h-4 w-4 mr-2" />
                    WooCommerce Format
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && preview && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Vorschau
                  <Badge className={getFormatBadge(preview.detected_format)}>
                    {preview.detected_format === 'shopify' ? 'Shopify' : 
                     preview.detected_format === 'woocommerce' ? 'WooCommerce' : 'Standard'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {preview.total_rows} Zeilen gefunden • {file?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Column Mapping */}
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Erkannte Spalten</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.column_mapping).map(([field, column]) => (
                      column && (
                        <div key={field} className="flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
                          <Check className="h-3 w-3" />
                          {field}: {column}
                        </div>
                      )
                    ))}
                  </div>
                  {preview.unmapped_fields?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {preview.unmapped_fields.map((field) => (
                        <div key={field} className="flex items-center gap-1 bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full text-sm">
                          <AlertCircle className="h-3 w-3" />
                          {field}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preview Table */}
                <div className="border rounded-lg overflow-auto max-h-80">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Preis</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Bestand</TableHead>
                        <TableHead>Kategorie</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.price || '-'}</TableCell>
                          <TableCell>{product.sku || '-'}</TableCell>
                          <TableCell>{product.stock || '-'}</TableCell>
                          <TableCell>{product.category || defaultCategory}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Options */}
            <Card>
              <CardHeader>
                <CardTitle>Import-Optionen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Bestehende Produkte aktualisieren</Label>
                    <p className="text-sm text-gray-500">Produkte mit gleicher SKU werden aktualisiert</p>
                  </div>
                  <Switch
                    checked={updateExisting}
                    onCheckedChange={setUpdateExisting}
                  />
                </div>
                
                <div>
                  <Label>Standard-Kategorie</Label>
                  <Input
                    value={defaultCategory}
                    onChange={(e) => setDefaultCategory(e.target.value)}
                    placeholder="Für Produkte ohne Kategorie"
                    className="mt-1 max-w-xs"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-4">
              <Button variant="outline" onClick={resetImport}>
                Abbrechen
              </Button>
              <Button onClick={handleImport} className="bg-[#8B7355] hover:bg-[#6D5A47]">
                {preview.total_rows} Produkte importieren
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Importing */}
        {step === 3 && (
          <Card>
            <CardContent className="py-16 text-center">
              <Loader2 className="h-16 w-16 animate-spin text-[#8B7355] mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-2">Produkte werden importiert...</h3>
              <p className="text-gray-600">Dies kann einen Moment dauern.</p>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Complete */}
        {step === 4 && result && (
          <div className="space-y-6">
            <Card>
              <CardContent className="py-12 text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                  result.imported > 0 ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  {result.imported > 0 ? (
                    <Check className="h-10 w-10 text-green-600" />
                  ) : (
                    <AlertCircle className="h-10 w-10 text-yellow-600" />
                  )}
                </div>
                
                <h3 className="text-2xl font-bold mb-4">
                  {result.imported > 0 ? 'Import abgeschlossen!' : 'Import mit Problemen'}
                </h3>
                
                <div className="flex justify-center gap-8 mb-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{result.imported}</p>
                    <p className="text-sm text-gray-500">Importiert</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-600">{result.skipped}</p>
                    <p className="text-sm text-gray-500">Übersprungen</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600">{result.errors?.length || 0}</p>
                    <p className="text-sm text-gray-500">Fehler</p>
                  </div>
                </div>
                
                <div className="flex justify-center gap-4">
                  <Button variant="outline" onClick={resetImport}>
                    Weitere Datei importieren
                  </Button>
                  <Link to="/vendor-dashboard">
                    <Button className="bg-[#8B7355] hover:bg-[#6D5A47]">
                      Zum Dashboard
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Errors */}
            {result.errors && result.errors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Fehler ({result.errors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-auto">
                    {result.errors.map((error, index) => (
                      <div key={index} className="flex items-start gap-2 text-sm p-2 bg-red-50 rounded">
                        <X className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                        <span>
                          <strong>Zeile {error.row}:</strong> {error.error}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Imported Products Preview */}
            {result.products && result.products.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Importierte Produkte (Vorschau)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Preis</TableHead>
                          <TableHead>SKU</TableHead>
                          <TableHead>Kategorie</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.products.map((product, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{product.name}</TableCell>
                            <TableCell>{formatEUR(product.price)}</TableCell>
                            <TableCell>{product.sku || '-'}</TableCell>
                            <TableCell>{product.category}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductImport;
